import * as FileSaver from 'file-saver'

const METRIC_LABEL_MAP = {
  pv: 'PV',
  uv: 'UV',
  clickUser: '点击人数',
  clickCount: '点击次数',
  sumStayTime: '总停留时长',
  avgStayTime: '平均停留时长',
  clickRate: '点击率',
  dropRate: '流失率',
  pvRate: 'PV占比',
  uvRate: 'UV占比',
  timeOnSite: '停留时长',
  totalTimeOnSite: '总停留时长',
  avg_stay_seconds: '平均停留(秒)',
  sevenDayReturnUser: '7日回访',
  value: 'Value'
}

export function getMetricLabel(key) {
  return METRIC_LABEL_MAP[key] || key
}

export const EXPORT_THEMES = [
  {
    key: 'ocean',
    label: '海洋蓝',
    headerFill: '#DCEBFF',
    subHeaderFill: '#EEF5FF',
    bodyFill: '#FFFFFF',
    accentFill: '#EAF7F2',
    headerFontColor: '#1F3A5F'
  },
  {
    key: 'forest',
    label: '森林绿',
    headerFill: '#DDF5E8',
    subHeaderFill: '#EEF9F3',
    bodyFill: '#FFFFFF',
    accentFill: '#F3FBF6',
    headerFontColor: '#174B35'
  },
  {
    key: 'sunset',
    label: '落日橙',
    headerFill: '#FFE6D6',
    subHeaderFill: '#FFF3EB',
    bodyFill: '#FFFFFF',
    accentFill: '#FFF8F3',
    headerFontColor: '#7A3E16'
  }
]

export function getThemeConfig(themeKey) {
  return EXPORT_THEMES.find((item) => item.key === themeKey) || EXPORT_THEMES[0]
}

export function buildExportFileName({ projectName, projectCode, dataLabel, startDate, endDate }) {
  const safeProject = (projectName || '项目').replace(/[\\/:*?"<>|]/g, '-')
  const safeLabel = (dataLabel || '数据').replace(/[\\/:*?"<>|]/g, '-')
  return `${safeProject}_${projectCode || 'unknown'}_${safeLabel}_${startDate}_${endDate}.xls`
}

export function exportWorkbook({ sheets, fileName }) {
  const workbookXml = buildWorkbookXml(sheets)
  const blob = new Blob([`\uFEFF${workbookXml}`], { type: 'application/vnd.ms-excel;charset=utf-8;' })
  FileSaver.saveAs(blob, fileName)
}

export function buildPivotSheet(options) {
  const {
    sheetName,
    orientation,
    dateList,
    metrics,
    items,
    valueMap,
    themeKey,
    includeMetricHeader = true,
    includeSumRow = false,
    includeDailySumRow = false,
    includeDedupRow = false,
    noCumDailyKeys = [],
    /** per-item 的 metrics 映射：{ [itemLabel]: string[] }，未提供则所有 item 用 metrics */
    itemMetrics = null
  } = options

  const theme = getThemeConfig(themeKey)
  const itemPlan = buildItemMetricPlan(items, metrics, itemMetrics)
  const baseSheet = orientation === 'date-column'
    ? buildDateColumnSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName, itemMetrics)
    : buildDateRowSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName, itemMetrics)

  if (!includeSumRow && !includeDailySumRow && !includeDedupRow) return baseSheet

  const rows = [...baseSheet.rows]
  const merges = [...baseSheet.merges]

  const readVal = (item, date, metric) => {
    const v = valueMap?.[item]?.[date]?.[metric]
    return typeof v === 'number' ? v : (Number(v) || 0)
  }

  const appendSummaryRowDateRow = (rowLabel, filterMetrics, fallbackCell = '-') => {
    const summaryRow = [makeCell(rowLabel, 'label', 'String')]
    dateList.forEach((date) => {
      itemPlan.forEach((colPlan) => {
        colPlan.metrics.forEach((metric) => {
          if (!filterMetrics.includes(metric)) {
            summaryRow.push(makeCell(fallbackCell, 'body', 'String'))
            return
          }
          let total = 0
          itemPlan.forEach((p) => {
            if (p.metrics.includes(metric)) total += readVal(p.item, date, metric)
          })
          summaryRow.push(makeCell(total, 'body', 'Number', resolveCellFormatByKey(metric)))
        })
      })
    })
    rows.push(summaryRow)
  }

  const appendSummaryRowDateColumn = (rowLabel, filterMetrics, fallbackCell = '-') => {
    const summaryRow = [makeCell(rowLabel, 'label', 'String')]
    itemPlan.forEach(({ item, metrics: itemMets }) => {
      itemMets.forEach((metric) => {
        if (filterMetrics.includes(metric)) {
          let total = 0
          dateList.forEach((date) => { total += readVal(item, date, metric) })
          summaryRow.push(makeCell(total, 'body', 'Number', resolveCellFormatByKey(metric)))
        } else {
          summaryRow.push(makeCell(fallbackCell, 'body', 'String'))
        }
      })
    })
    rows.push(summaryRow)
  }

  // 全量指标（includeSumRow 时不区分 noCumDailyKeys）
  if (includeSumRow) {
    if (orientation === 'date-row') {
      appendSummaryRowDateRow('汇总合计', metrics)
    } else {
      appendSummaryRowDateColumn('汇总合计', metrics)
    }
  }

  // 按天累加汇总：只追加一行，值等于总和，仅可累加指标（排除停留时长等）
  if (includeDailySumRow) {
    const cumMetrics = metrics.filter((m) => !noCumDailyKeys.includes(m))
    if (cumMetrics.length) {
      if (orientation === 'date-row') {
        appendSummaryRowDateRow('按天累加汇总', cumMetrics)
      } else {
        appendSummaryRowDateColumn('按天累加汇总', cumMetrics)
      }
    }
  }

  // 汇总去重：只追加一行，值等于总和，仅可去重指标（排除停留时长等）
  if (includeDedupRow) {
    const dedupMetrics = metrics.filter((m) => !noCumDailyKeys.includes(m))
    if (dedupMetrics.length) {
      if (orientation === 'date-row') {
        appendSummaryRowDateRow('汇总去重', dedupMetrics)
      } else {
        appendSummaryRowDateColumn('汇总去重', dedupMetrics)
      }
    }
  }

  return {
    ...baseSheet,
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < (metrics.length > 1 && includeMetricHeader ? 2 : 1) ? 24 : 22))
  }
}

export function buildTableSheetFromRows(options) {
  const {
    sheetName,
    rows = [],
    themeKey
  } = options
  const theme = getThemeConfig(themeKey)
  const columns = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key))
      return set
    }, new Set())
  )
  const header = columns.map((key) => makeCell(getMetricLabel(key), 'header-main', 'String'))
  const bodyRows = rows.map((row) => columns.map((key, index) => {
    const value = row?.[key]
    const type = typeof value === 'number' ? 'Number' : 'String'
    return makeCell(value ?? '', index === 0 ? 'label' : 'body', type, resolveCellFormatByKey(key))
  }))

  const sheetRows = [header, ...bodyRows]
  return {
    name: sanitizeSheetName(sheetName),
    rows: sheetRows,
    merges: [],
    colWidths: computeColWidths(sheetRows),
    rowHeights: sheetRows.map((_, index) => (index === 0 ? 24 : 22)),
    theme
  }
}

export function buildGroupedDateReportSheet(options) {
  const {
    sheetName,
    dateList = [],
    groups = [],
    includeDailySumRow = false,
    includeDedupRow = false,
    themeKey
  } = options

  const theme = getThemeConfig(themeKey)
  const rows = []
  const merges = []

  const head1 = [makeCell('日期', 'header-main', 'String')]
  const head2 = [makeCell('日期', 'header-sub', 'String')]
  let col = 1

  groups.forEach((group) => {
    const metrics = group.metrics || []
    if (!metrics.length) return
    head1.push(makeCell(group.label, 'header-main', 'String'))
    for (let i = 1; i < metrics.length; i += 1) {
      head1.push(makePlaceholderCell())
    }
    metrics.forEach((metric) => {
      head2.push(makeCell(metric.label, 'header-sub', 'String'))
    })
    merges.push({ row: 0, col, across: metrics.length - 1, down: 0 })
    col += metrics.length
  })

  rows.push(head1, head2)

  dateList.forEach((date) => {
    const row = [makeCell(date, 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.valuesByDate?.[date]?.[metric.key] ?? 0, 'body', 'Number', metric.format || resolveCellFormatByKey(metric.key)))
      })
    })
    rows.push(row)
  })

  if (includeDailySumRow) {
    const row = [makeCell('按天累加数据', 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.summary?.dailySum?.[metric.key] ?? 0, 'body', 'Number', metric.format || resolveCellFormatByKey(metric.key)))
      })
    })
    rows.push(row)
  }

  if (includeDedupRow) {
    const row = [makeCell('去重数据', 'label', 'String')]
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        row.push(makeCell(group.summary?.dedup?.[metric.key] ?? 0, 'body', 'Number', metric.format || resolveCellFormatByKey(metric.key)))
      })
    })
    rows.push(row)
  }

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < 2 ? 24 : 22)),
    theme
  }
}

function buildItemMetricPlan(items, metrics, itemMetrics) {
  return items.map((item) => ({
    item,
    metrics: (itemMetrics?.[item]?.length) ? [...itemMetrics[item]] : [...metrics]
  }))
}

function colsPerDateFromPlan(itemPlan) {
  return itemPlan.reduce((sum, p) => sum + p.metrics.length, 0)
}

function buildDateRowSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName, itemMetrics = null) {
  const rows = []
  const merges = []
  const itemPlan = buildItemMetricPlan(items, metrics, itemMetrics)
  const colsPerDate = colsPerDateFromPlan(itemPlan)
  const useSubHeader = includeMetricHeader && colsPerDate > itemPlan.length

  if (useSubHeader) {
    const head1 = [makeCell('对象', 'header-main', 'String')]
    const head2 = [makeCell('对象', 'header-sub', 'String')]
    const head3 = [makeCell('对象', 'header-sub', 'String')]
    dateList.forEach((date) => {
      let col = head1.length
      head1.push(makeCell(date, 'header-main', 'String'))
      for (let i = 1; i < colsPerDate; i += 1) head1.push(makePlaceholderCell())
      if (colsPerDate > 1) merges.push({ row: 0, col, across: colsPerDate - 1, down: 0 })

      itemPlan.forEach(({ item, metrics: itemMets }) => {
        const mlen = itemMets.length
        const col2 = head2.length
        head2.push(makeCell(item, 'header-sub', 'String'))
        for (let i = 1; i < mlen; i += 1) head2.push(makePlaceholderCell())
        if (mlen > 1) merges.push({ row: 1, col: col2, across: mlen - 1, down: 0 })
        itemMets.forEach((metric) => {
          head3.push(makeCell(getMetricLabel(metric), 'header-sub', 'String'))
        })
      })
    })
    rows.push(head1, head2, head3)
  } else if (includeMetricHeader && colsPerDate > 1) {
    const head1 = [makeCell('对象', 'header-main', 'String')]
    const head2 = [makeCell('对象', 'header-sub', 'String')]
    dateList.forEach((date) => {
      const col = head1.length
      head1.push(makeCell(date, 'header-main', 'String'))
      for (let i = 1; i < colsPerDate; i += 1) head1.push(makePlaceholderCell())
      if (colsPerDate > 1) merges.push({ row: 0, col, across: colsPerDate - 1, down: 0 })
      itemPlan.forEach(({ metrics: itemMets }) => {
        itemMets.forEach((metric) => {
          head2.push(makeCell(getMetricLabel(metric), 'header-sub', 'String'))
        })
      })
    })
    rows.push(head1, head2)
  } else {
    rows.push([makeCell('对象', 'header-main', 'String'), ...dateList.map((date) => makeCell(date, 'header-main', 'String'))])
  }

  const headerRowCount = rows.length

  itemPlan.forEach(({ item, metrics: itemMets }) => {
    const row = [makeCell(item, 'label', 'String')]
    dateList.forEach((date) => {
      itemPlan.forEach((colPlan) => {
        colPlan.metrics.forEach((metric) => {
          if (colPlan.item !== item) {
            row.push(makeCell('', 'body', 'String'))
            return
          }
          const v = readMetricValue(valueMap, item, date, metric)
          if (v === null) {
            row.push(makeCell('-', 'body', 'String'))
          } else {
            row.push(makeCell(v, 'body', 'Number', resolveCellFormatByKey(metric)))
          }
        })
      })
    })
    rows.push(row)
  })

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < headerRowCount ? 24 : 22)),
    theme
  }
}

function buildDateColumnSheet(dateList, metrics, items, valueMap, theme, includeMetricHeader, sheetName, itemMetrics = null) {
  const rows = []
  const merges = []
  const itemPlan = buildItemMetricPlan(items, metrics, itemMetrics)
  const multiMetricHeader = includeMetricHeader && itemPlan.some((p) => p.metrics.length > 1)

  if (multiMetricHeader) {
    const head1 = [makeCell('日期', 'header-main', 'String')]
    const head2 = [makeCell('日期', 'header-sub', 'String')]
    let col = 1
    itemPlan.forEach(({ item, metrics: itemMets }) => {
      const mlen = itemMets.length
      head1.push(makeCell(item, 'header-main', 'String'))
      for (let i = 1; i < mlen; i += 1) head1.push(makePlaceholderCell())
      itemMets.forEach((metric) => {
        head2.push(makeCell(getMetricLabel(metric), 'header-sub', 'String'))
      })
      if (mlen > 1) merges.push({ row: 0, col, across: mlen - 1, down: 0 })
      col += mlen
    })
    rows.push(head1, head2)
  } else {
    rows.push([
      makeCell('日期', 'header-main', 'String'),
      ...itemPlan.map((p) => makeCell(p.item, 'header-main', 'String'))
    ])
  }

  const headerRowCount = rows.length

  dateList.forEach((date) => {
    const row = [makeCell(date, 'label', 'String')]
    itemPlan.forEach(({ item, metrics: itemMets }) => {
      itemMets.forEach((metric) => {
        const v = readMetricValue(valueMap, item, date, metric)
        if (v === null) {
          row.push(makeCell('-', 'body', 'String'))
        } else {
          row.push(makeCell(v, 'body', 'Number', resolveCellFormatByKey(metric)))
        }
      })
    })
    rows.push(row)
  })

  return {
    name: sanitizeSheetName(sheetName),
    rows,
    merges,
    colWidths: computeColWidths(rows),
    rowHeights: rows.map((_, index) => (index < headerRowCount ? 24 : 22)),
    theme
  }
}

function readMetricValue(valueMap, item, date, metric) {
  const v = valueMap?.[item]?.[date]?.[metric]
  // undefined 表示该渠道未配置此指标，返回特殊标记
  if (v === undefined) return null
  return v ?? 0
}

function makeCell(value, role, type, format = '') {
  return { value, role, type, format }
}

function resolveCellFormatByKey(key) {
  const metricKey = String(key || '')
  // 百分比类：率类指标，Excel 里以 0.00% 格式呈现
  if (['clickRate', 'dropRate', 'pvRate', 'uvRate'].includes(metricKey)) {
    return 'percent'
  }
  // 小数类：平均停留秒数
  if (['avgStayTime', 'avg_stay_seconds'].includes(metricKey)) {
    return 'decimal'
  }
  // 整数类：PV/UV 及停留时长累计值
  if (['timeOnSite', 'totalTimeOnSite', 'sumStayTime', 'pv', 'uv', 'clickUser', 'clickCount', 'sevenDayReturnUser', 'value'].includes(metricKey)) {
    return 'integer'
  }
  return ''
}

function makePlaceholderCell() {
  return { placeholder: true }
}

function computeColWidths(rows) {
  const maxCols = Math.max(...rows.map((row) => row.length), 0)
  return Array.from({ length: maxCols }, (_, colIndex) => {
    let maxLength = 10
    rows.forEach((row) => {
      if (row[colIndex]?.placeholder) return
      const value = row[colIndex]?.value == null ? '' : String(row[colIndex].value)
      maxLength = Math.max(maxLength, value.length + 2)
    })
    return Math.min(maxLength * 7, 220)
  })
}

function buildWorkbookXml(sheets) {
  const styles = buildStyleXml()
  const worksheets = sheets.map((sheet) => buildWorksheetXml(sheet)).join('')

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>Codex</Author>
  <Created>${escapeXml(new Date().toISOString())}</Created>
 </DocumentProperties>
 <ExcelWorkbook xmlns="urn:schemas-microsoft-com:office:excel">
  <ProtectStructure>False</ProtectStructure>
  <ProtectWindows>False</ProtectWindows>
 </ExcelWorkbook>
 ${styles}
 ${worksheets}
</Workbook>`
}

function buildStyleXml() {
  const themeStyles = EXPORT_THEMES.map((theme) => {
    const suffix = sanitizeStyleKey(theme.key)
    return `
  <Style ss:ID="header-main-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="13" ss:Color="${theme.headerFontColor}"/>
   <Interior ss:Color="${theme.headerFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="header-sub-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="11" ss:Color="${theme.headerFontColor}"/>
   <Interior ss:Color="${theme.subHeaderFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="label-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Left"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="${theme.accentFill}" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="body-${suffix}">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="${theme.bodyFill}" ss:Pattern="Solid"/>
  </Style>`
  + `
  <Style ss:ID="body-integer-${suffix}" ss:Parent="body-${suffix}">
   <NumberFormat ss:Format="#,##0"/>
  </Style>
  <Style ss:ID="body-decimal-${suffix}" ss:Parent="body-${suffix}">
   <NumberFormat ss:Format="#,##0.00"/>
  </Style>
  <Style ss:ID="body-percent-${suffix}" ss:Parent="body-${suffix}">
   <NumberFormat ss:Format="0.00%"/>
  </Style>`
  }).join('')

  return `<Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
   <Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="header-main">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="13" ss:Color="#1F2937"/>
  </Style>
  <Style ss:ID="header-sub">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="11" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="label">
   <Alignment ss:Vertical="Center" ss:Horizontal="Left"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Bold="1" ss:Size="10" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="body">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D7DEE7"/>
   </Borders>
   <Font ss:FontName="Microsoft YaHei" ss:Size="10" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="body-integer" ss:Parent="body">
   <NumberFormat ss:Format="#,##0"/>
  </Style>
  <Style ss:ID="body-decimal" ss:Parent="body">
   <NumberFormat ss:Format="#,##0.00"/>
  </Style>
  <Style ss:ID="body-percent" ss:Parent="body">
   <NumberFormat ss:Format="0.00%"/>
  </Style>
  ${themeStyles}
 </Styles>`
}

function buildWorksheetXml(sheet) {
  const rowsXml = sheet.rows
    .map((row, rowIndex) => {
      const height = sheet.rowHeights?.[rowIndex]
      let visualColIndex = 0
      const cellsXml = row
        .map((cell, colIndex) => {
          visualColIndex += 1
          if (cell?.placeholder) {
            return ''
          }
          const merge = sheet.merges.find((item) => item.row === rowIndex && item.col === colIndex)
          const styleId = resolveStyleId(cell, rowIndex, colIndex, sheet.theme)
          const mergeAcross = merge ? ` ss:MergeAcross="${merge.across}"` : ''
          const mergeDown = merge ? ` ss:MergeDown="${merge.down}"` : ''
          const dataType = cell.type || inferType(cell.value)
          const indexAttr = visualColIndex === colIndex + 1 ? '' : ` ss:Index="${visualColIndex}"`
          return `<Cell${indexAttr} ss:StyleID="${styleId}"${mergeAcross}${mergeDown}><Data ss:Type="${dataType}">${escapeXml(formatCellValue(cell.value, dataType))}</Data></Cell>`
        })
        .join('')
      const heightAttr = height ? ` ss:AutoFitHeight="0" ss:Height="${height}"` : ''
      return `<Row${heightAttr}>${cellsXml}</Row>`
    })
    .join('')

  const columnsXml = (sheet.colWidths || [])
    .map((width) => `<Column ss:AutoFitWidth="0" ss:Width="${width}"/>`)
    .join('')

  return `<Worksheet ss:Name="${escapeXml(sheet.name)}">
  <Table>${columnsXml}${rowsXml}</Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <Selected/>
   <ProtectObjects>False</ProtectObjects>
   <ProtectScenarios>False</ProtectScenarios>
  </WorksheetOptions>
 </Worksheet>`
}

function resolveStyleId(cell, rowIndex, colIndex, theme) {
  const baseRole = cell.role || 'body'
  const roleWithFormat = cell.format && baseRole === 'body' ? `${baseRole}-${cell.format}` : baseRole
  if (!theme) return roleWithFormat
  const suffix = sanitizeStyleKey(theme.key || 'theme')
  return `${roleWithFormat}-${suffix}`
}

function sanitizeStyleKey(value) {
  return String(value || '').replace(/[^a-zA-Z0-9_-]/g, '')
}

function inferType(value) {
  return typeof value === 'number' ? 'Number' : 'String'
}

function formatCellValue(value, type) {
  if (value == null) return ''
  if (type === 'Number') return Number(value)
  return String(value)
}

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function sanitizeSheetName(name) {
  return String(name || 'sheet').replace(/[\\/?*[\]:]/g, '_').slice(0, 28)
}

function compareBehaviorSortKey(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'en')
}

function sortBehaviorNameList(list = []) {
  return [...list].sort(compareBehaviorSortKey)
}

/**
 * 保存进清单前：勾选排序时重排独立行为、组内成员、合并组顺序（导出按此顺序，不再二次排序）
 */
export function finalizeBehaviorSheetConfig(sheet) {
  if (!sheet || sheet.category !== 'behavior' || sheet.sortBehaviors !== true) {
    return sheet
  }
  const mergedBehaviors = [...(sheet.mergedBehaviors || [])]
    .filter((m) => m.behaviors?.length)
    .map((m) => ({
      ...m,
      name: m.name || m.behaviors.join('+'),
      behaviors: sortBehaviorNameList(m.behaviors || []),
      keepOriginal: !!m.keepOriginal
    }))
    .sort((a, b) => {
      const ka = (a.name || '').trim() || (a.behaviors || []).join('+')
      const kb = (b.name || '').trim() || (b.behaviors || []).join('+')
      return compareBehaviorSortKey(ka, kb)
    })
  return {
    ...sheet,
    behaviors: sortBehaviorNameList(sheet.behaviors || []),
    mergedBehaviors
  }
}
